// The half of the footer that is identical in the SPA (`ui/Footer.tsx`) and on the four prerendered marketing pages (`app/routes/splash-chrome.tsx`): brand block, Legal column, copyright rule, and the two link primitives. It lives under `src/ui/Static/` because that is the proven prerender-safe boundary — `Section.tsx` beside it is already imported by both module graphs — and because a drifting Legal list is a compliance defect, not a cosmetic one.
// Each footer still owns its own middle column: the SPA's Connect list needs `utils/config.ts`'s `COMMUNITY_LINK` (Amplify/env config, not prerender-safe) and the marketing one needs `SPLASH_NAV`, which nothing under `src/ui/` may import from `app/routes/`.
import type {ReactNode} from "react";
import {Link as RouterLink} from "react-router";
import Box from "@mui/material/Box";
import StoreBadges from "./StoreBadges";
import Stack from "@mui/material/Stack";
import Divider from "@mui/material/Divider";
import Typography from "@mui/material/Typography";
import MUILink from "@mui/material/Link";
import PrivacyIcon from "@mui/icons-material/LockOutlined";
import TermsIcon from "@mui/icons-material/BalanceOutlined";
import DisclaimerIcon from "@mui/icons-material/BackHandOutlined";
import PrivacyTipOutlinedIcon from "@mui/icons-material/PrivacyTipOutlined";
import SupportIcon from "@mui/icons-material/HelpOutlineOutlined";

/** The Consumer Health Data Privacy row is the homepage's MHMDA link (`home.dom.test.tsx` pins it on `/`). */
const LEGAL = [
  {to: '/privacy', label: 'Privacy Policy', icon: <PrivacyIcon fontSize="small"/>},
  {to: '/terms', label: 'Terms & Conditions', icon: <TermsIcon fontSize="small"/>},
  {to: '/disclaimer', label: 'Disclaimer', icon: <DisclaimerIcon fontSize="small"/>},
  {to: '/consumer-health-privacy', label: 'Consumer Health Data Privacy', icon: <PrivacyTipOutlinedIcon fontSize="small"/>},
  {to: '/support', label: 'Support', icon: <SupportIcon fontSize="small"/>},
] as const

export function FooterHeading({children}: {children: ReactNode}) {
  return <Typography variant="overline" sx={{display: 'block', mb: 2, fontWeight: 700, opacity: 0.5}}>{children}</Typography>
}

/** `color="inherit"`, never `primary.contrastText`: both footers sit on the scheme-INVARIANT navy `background.elevated` and set `common.white` on their wrapper, while `contrastText` flips to navy in the dark scheme — navy on navy. `Static/Section.tsx` carries the same warning. */
export function FooterLink({to, external, icon, children}: {to: string; external?: boolean; icon?: ReactNode; children: ReactNode}) {
  const link = external
    ? <MUILink color="inherit" underline="hover" href={to} target="_blank">{children}</MUILink>
    : <MUILink color="inherit" underline="hover" component={RouterLink} to={to}>{children}</MUILink>
  return <Stack direction="row" spacing={1} sx={{alignItems: 'center', mb: 0.5}}>{icon}{link}</Stack>
}

/** Heading included — this is the one footer column that must stay byte-identical on every surface. */
export function FooterLegal() {
  return <>
    <FooterHeading>Legal</FooterHeading>
    {LEGAL.map(l => <FooterLink key={l.to} to={l.to} icon={l.icon}>{l.label}</FooterLink>)}
  </>
}

/** Logo, wordmark, tagline. `component="span"` is load-bearing: `variant="h6"` alone emits a REAL `<h6>`, putting a logotype into the document outline above the page's own `<h2>`s. */
export function FooterBrand() {
  return <>
    <Box sx={{display: 'flex', alignItems: 'center', gap: 1, mb: 2}}>
      <Box component="img" src="/logo.png" alt="Gnothi" sx={{height: 28, width: 28, borderRadius: '6px', objectFit: 'contain'}}/>
      <Typography variant="h6" component="span" sx={{fontFamily: 'Outfit, sans-serif', fontWeight: 600, fontSize: '1.1rem', letterSpacing: '0.02em'}}>Gnothi</Typography>
    </Box>
    <Typography variant="body1" sx={{opacity: 0.8, maxWidth: 300}}>Create a continuing story or audio course, then read or listen chapter by chapter.</Typography>
    <Box sx={{mt: 2}}><StoreBadges/></Box>
  </>
}

// Module scope so every footer in a realm agrees on one value. It does NOT prevent a prerender/hydrate mismatch — the const re-evaluates in the browser realm too, so the prerendered HTML carries the BUILD year until hydration. That one-day New Year diff is accepted; only a bundler-injected constant would remove it.
const YEAR = new Date().getFullYear()

export function FooterCopyright() {
  return <>
    <Divider sx={{borderColor: 'common.white', opacity: 0.1, my: 4}}/>
    <Typography variant="body2" sx={{opacity: 0.6}}>Copyright © 2019-{YEAR} OCDevel, LLC</Typography>
  </>
}
